import { useEffect, useRef, useState } from "react";
import { Crown, ArrowsLeftRight, CaretDown, House, MagnifyingGlass, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import { superApi } from "@/lib/api";

/**
 * Top-bar company switcher visible ONLY to super_admin users.
 * Pulls all companies, sets a sticky `viewing_company_id` server-side, then
 * triggers a hard reload so the WS/API context re-binds to the new tenant.
 */
export default function CompanySwitcher({ user, onSwitched }) {
  const [open, setOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    superApi.listCompanies().then(setCompanies).catch(() => setCompanies([]));
    const onDocClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const isViewingOther = !!user?.is_viewing_other;
  const homeId = user?.real_company_id || user?.company_id;
  const activeId = user?.company_id;

  const switchTo = async (companyId) => {
    setBusy(true);
    try {
      await superApi.viewCompany(companyId || null);
      const target = companies.find((c) => c.id === companyId);
      toast.success(companyId ? `Viewing ${target?.name || "company"}` : "Returned home");
      // Hard reload so the auth context + WS re-bind to the new effective tenant
      onSwitched ? onSwitched() : window.location.reload();
    } catch (e) {
      toast.error("Failed to switch company");
    } finally {
      setBusy(false);
    }
  };

  const filtered = companies.filter((c) =>
    !search || c.name.toLowerCase().includes(search.toLowerCase()) || c.slug.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div ref={ref} className="relative" data-testid="company-switcher">
      <button
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-2 px-3 py-1.5 border text-[10px] uppercase tracking-[0.2em] font-bold transition-colors ${
          isViewingOther
            ? "border-[#ff8c00] bg-[#ff8c00]/10 text-[#ff8c00] hover:bg-[#ff8c00]/20"
            : "border-[#4682b4] bg-[#4682b4]/10 text-[#4682b4] hover:bg-[#4682b4]/20"
        }`}
        data-testid="company-switcher-trigger"
      >
        <Crown size={11} weight="fill" />
        <span className="hidden md:inline truncate max-w-[140px]">
          {isViewingOther ? `→ ${user?.company_name}` : user?.company_name || "—"}
        </span>
        <CaretDown size={10} weight="bold" />
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[320px] bg-[#0d0d0d] border border-[#2a2a2a] shadow-2xl z-[1000]" data-testid="company-switcher-panel">
          <div className="px-3 py-2 border-b border-[#2a2a2a] flex items-center gap-2">
            <ArrowsLeftRight size={12} className="text-[#ff8c00]" weight="bold" />
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#ff8c00] font-bold">View as another company</span>
          </div>

          {isViewingOther && (
            <button
              onClick={() => switchTo(null)}
              disabled={busy}
              className="w-full px-3 py-2 border-b border-[#2a2a2a] text-left hover:bg-[#1a1a1a] flex items-center gap-2"
              data-testid="company-switcher-home"
            >
              <House size={12} weight="bold" className="text-[#4682b4]" />
              <div className="flex-1">
                <div className="text-xs font-bold">Return to your home tenant</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a]">{user?.real_company_name || "—"}</div>
              </div>
            </button>
          )}

          <div className="px-3 py-2 border-b border-[#2a2a2a]">
            <div className="relative">
              <MagnifyingGlass size={11} className="absolute left-2 top-1/2 -translate-y-1/2 text-[#71717a]" />
              <input
                type="text"
                placeholder="Search companies…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-[#1a1a1a] border border-[#2a2a2a] focus:border-[#ff8c00] outline-none pl-7 pr-2 py-1.5 text-xs font-mono"
                data-testid="company-switcher-search"
              />
            </div>
          </div>

          <div className="max-h-[280px] overflow-y-auto">
            {filtered.length === 0 && (
              <div className="px-3 py-4 text-center text-[10px] uppercase tracking-[0.2em] text-[#71717a]">No matches</div>
            )}
            {filtered.map((c) => {
              const isActive = c.id === activeId;
              const isHome = c.id === homeId;
              return (
                <button
                  key={c.id}
                  onClick={() => !isActive && switchTo(c.id)}
                  disabled={busy || isActive}
                  className={`w-full px-3 py-2 text-left flex items-center justify-between gap-2 transition-colors ${
                    isActive ? "bg-[#ff8c00]/10 cursor-default" : "hover:bg-[#1a1a1a]"
                  }`}
                  data-testid={`company-switcher-option-${c.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="font-bold text-xs truncate flex items-center gap-2">
                      {c.name}
                      {isHome && <span className="px-1 py-0.5 border border-[#4682b4] text-[#4682b4] text-[8px] uppercase tracking-[0.2em]">Home</span>}
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#71717a] font-mono truncate">
                      {c.slug} · {c.user_count}u · {c.asset_count}a · {c.job_count}j
                    </div>
                  </div>
                  {isActive && <CheckCircle size={14} weight="fill" className="text-[#ff8c00] shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
